/**
 * One brokerage as C-BROKER-SEARCH holds it: every fact with the statement that won it, every list member and contact with
 * each source that stated it, who said what and when, and the other brokerages it is tied to. Five views of one card:
 *
 *   Overview     the facts and lists, each with its provenance (B-9)
 *   Contacts     every live contact, by cohort, paged
 *   Sources      one row per source: what it stated, first and last
 *   Timeline     the provenance in time order, newest first
 *   Connections  brokerages sharing a domain, an email or phone, or a family
 *
 * It is the card's provenance, not a log of every update: a replaced value keeps only its newest statement.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Link from '@mui/joy/Link'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Table from '@mui/joy/Table'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import { BROKER_COHORTS, type BrokerSearchInspectorResponse, type BrokerSearchMember, type BrokerSearchValue } from '@numeo/types-core'
import * as React from 'react'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { ErrorState, Loading, Stat, StatRow, formatNumber, useLoadPipelineData } from '../../load-pipeline/components/shared'
import { brokeragePath, said, statusColor, when } from '../components/shared'

const VIEWS = ['overview', 'contacts', 'sources', 'timeline', 'connections'] as const
type View = (typeof VIEWS)[number]
const VIEW_LABELS: Record<View, string> = { overview: 'Overview', contacts: 'Contacts', sources: 'Sources', timeline: 'Timeline', connections: 'Connections' }

/** A source and when it stated something, as small chips. */
function Sightings({ sources }: { sources: Record<string, { firstSeenAt: string; lastSeenAt: string }> }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {Object.entries(sources).map(([source, seen]) => (
                <Chip key={source} size="sm" variant="soft" title={`first ${when(seen.firstSeenAt)} · last ${when(seen.lastSeenAt)}`}>
                    {source}
                </Chip>
            ))}
        </Box>
    )
}

/** One value and its provenance: who stated the winning value, when it was last confirmed, when it last changed. */
function ValueRow<T>({ label, value, show }: { label: string; value: BrokerSearchValue<T> | undefined; show: (v: T) => string }) {
    if (!value) return null
    return (
        <tr>
            <td>{label}</td>
            <td>{show(value.value)}</td>
            <td>{said(value.by)}</td>
            <td>{when(value.lastConfirmedAt)}</td>
            <td>{when(value.lastChangedAt)}</td>
        </tr>
    )
}

/** A list on the card (trade names, domains, offices, assets): each member with every source that stated it. */
function MemberTable<T>({
    title,
    members,
    show,
    extra,
}: {
    title: string
    members: BrokerSearchMember<T>[]
    show: (v: T) => React.ReactNode
    extra?: (v: T) => React.ReactNode
}) {
    return (
        <Card variant="outlined" sx={{ p: 1.5 }}>
            <Typography level="title-sm">
                {title} <Typography textColor="text.tertiary">{members.length}</Typography>
            </Typography>
            {members.length === 0 ? (
                <Typography level="body-sm" textColor="text.tertiary">
                    None stated.
                </Typography>
            ) : (
                <Table size="sm" borderAxis="none">
                    <tbody>
                        {members.map((m, i) => (
                            <tr key={i}>
                                <td>
                                    {show(m.value)} {extra?.(m.value)}
                                </td>
                                <td>
                                    <Sightings sources={m.sources} />
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{when(m.lastSeenAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Card>
    )
}

function Overview({ card }: { card: BrokerSearchInspectorResponse }) {
    return (
        <>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', mb: 2 }}>
                <Table size="sm">
                    <thead>
                        <tr>
                            <th style={{ width: 180 }}>Fact</th>
                            <th>Is</th>
                            <th>Stated by</th>
                            <th>Last confirmed</th>
                            <th>Last changed</th>
                        </tr>
                    </thead>
                    <tbody>
                        <ValueRow label="Legal name" value={card.legalName} show={(v) => v} />
                        <ValueRow
                            label="Authority"
                            value={card.authority}
                            show={(v) =>
                                [v.status, v.grantedAt && `granted ${v.grantedAt}`, v.revokedAt && `revoked ${v.revokedAt}`].filter(Boolean).join(' · ')
                            }
                        />
                        <ValueRow label="Registered address" value={card.registeredAddress} show={(v) => Object.values(v).join(', ')} />
                        <ValueRow label="Officers" value={card.officers} show={(v) => v.join(', ')} />
                        <ValueRow
                            label="Bond"
                            value={card.bond}
                            show={(v) =>
                                [
                                    v.form === 'TRUST_FUND' ? 'trust fund' : 'surety bond',
                                    v.provider,
                                    `since ${v.effectiveAt}`,
                                    v.cancelsAt && `cancels ${v.cancelsAt}`,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')
                            }
                        />
                        <ValueRow
                            label="Safety"
                            value={card.safety}
                            show={(v) => `${v.rating ?? 'no rating'} · allowed to operate: ${v.allowedToOperate ? 'yes' : 'no'}`}
                        />
                        {Object.entries(card.credit).map(([bureau, rating]) => (
                            <ValueRow
                                key={bureau}
                                label={`Credit · ${bureau}`}
                                value={rating}
                                show={(v) => `${v.rating}${v.daysToPay !== undefined ? ` · ${v.daysToPay} days to pay` : ''} · rated ${v.ratedAt.slice(0, 10)}`}
                            />
                        ))}
                        <ValueRow
                            label="Payment terms"
                            value={card.paymentTerms}
                            show={(v) =>
                                [
                                    v.netDays !== undefined && `net ${v.netDays}`,
                                    v.quickPayFeePercent !== undefined && `quick pay ${v.quickPayFeePercent}%`,
                                    v.remitTo && `remit to ${v.remitTo}`,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')
                            }
                        />
                        <ValueRow
                            label="Web"
                            value={card.web}
                            show={(v) =>
                                [v.siteStatus, v.mailProvider && `mail ${v.mailProvider}`, v.domainRegisteredAt && `domain since ${v.domainRegisteredAt}`]
                                    .filter(Boolean)
                                    .join(' · ')
                            }
                        />
                        <ValueRow label="Family" value={card.family} show={(v) => `${v.parentName} (${v.familyKey})`} />
                        <ValueRow label="Suppressed" value={card.suppressed} show={(v) => (v ? 'yes' : 'no')} />
                    </tbody>
                </Table>
            </Sheet>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                <MemberTable title="Trade names" members={card.tradeNames} show={(v) => v} />
                <MemberTable
                    title="Domains"
                    members={card.domains}
                    show={(v) => v.value}
                    extra={(v) => (
                        <>
                            {card.ownedDomains.includes(v.value) ? (
                                <Chip size="sm" color="success" variant="soft">
                                    owned
                                </Chip>
                            ) : null}{' '}
                            {v.role ? <Chip size="sm">{v.role}</Chip> : null}
                        </>
                    )}
                />
                <MemberTable
                    title="Offices"
                    members={card.offices}
                    show={(v) => [v.kind, [v.city, v.state].filter(Boolean).join(', ') || v.officeKey].filter(Boolean).join(' · ')}
                />
                <MemberTable
                    title="Assets"
                    members={card.assets}
                    show={(v) => (
                        <>
                            {v.kind}{' '}
                            <Link href={v.url} target="_blank" rel="noreferrer">
                                {v.url}
                            </Link>
                        </>
                    )}
                />
            </Box>
        </>
    )
}

function Contacts({
    card,
    cohort,
    page,
    onCohort,
    onPage,
}: {
    card: BrokerSearchInspectorResponse
    cohort: string
    page: number
    onCohort: (c: string) => void
    onPage: (p: number) => void
}) {
    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                {BROKER_COHORTS.map((c) => (
                    <Chip key={c} size="sm" variant={cohort === c ? 'solid' : 'soft'} onClick={() => onCohort(cohort === c ? '' : c)}>
                        {c} {formatNumber(card.contactCounts[c] ?? 0)}
                    </Chip>
                ))}
                <Select size="sm" value={cohort} onChange={(_, v) => onCohort(v ?? '')} sx={{ ml: 'auto', minWidth: 160 }}>
                    <Option value="">Every cohort</Option>
                    {BROKER_COHORTS.map((c) => (
                        <Option key={c} value={c}>
                            {c}
                        </Option>
                    ))}
                </Select>
            </Box>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm" stripe="odd">
                    <thead>
                        <tr>
                            <th>Contact</th>
                            <th style={{ width: 130 }}>Cohort</th>
                            <th>Person</th>
                            <th>Sources</th>
                            <th>First seen</th>
                            <th>Last seen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {card.contacts.map((c) => (
                            <tr key={`${c.kind}|${c.value}`}>
                                <td>
                                    {c.kind} <code>{c.value}</code>
                                    {c.label ? ` · ${c.label}` : ''}
                                </td>
                                <td>{c.cohort}</td>
                                <td>{c.person ? [c.person.value.name, c.person.value.title, c.person.value.phone].filter(Boolean).join(' · ') : '—'}</td>
                                <td>
                                    <Sightings sources={c.sources} />
                                </td>
                                <td>{when(c.firstSeenAt)}</td>
                                <td>{when(c.lastSeenAt)}</td>
                            </tr>
                        ))}
                        {card.contacts.length === 0 ? (
                            <tr>
                                <td colSpan={6}>No live contacts{cohort ? ` in ${cohort}` : ''}.</td>
                            </tr>
                        ) : null}
                    </tbody>
                </Table>
            </Sheet>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button size="sm" variant="plain" disabled={page === 0} onClick={() => onPage(page - 1)}>
                    Previous
                </Button>
                <Button size="sm" variant="plain" disabled={!card.moreContacts} onClick={() => onPage(page + 1)}>
                    Next
                </Button>
            </Box>
        </>
    )
}

function Sources({ card }: { card: BrokerSearchInspectorResponse }) {
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table size="sm" stripe="odd">
                <thead>
                    <tr>
                        <th style={{ width: 140 }}>Source</th>
                        <th style={{ width: 180 }}>First stated</th>
                        <th style={{ width: 180 }}>Last stated</th>
                        <th>What it stated</th>
                    </tr>
                </thead>
                <tbody>
                    {card.sources.map((s) => (
                        <tr key={s.source}>
                            <td>
                                <Chip size="sm" variant="soft">
                                    {s.source}
                                </Chip>
                            </td>
                            <td>{when(s.firstSeenAt)}</td>
                            <td>{when(s.lastSeenAt)}</td>
                            <td>
                                <Typography level="body-sm">{s.stated.join(' · ')}</Typography>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Sheet>
    )
}

function Timeline({ card }: { card: BrokerSearchInspectorResponse }) {
    const [source, setSource] = React.useState('')
    const sources = [...new Set(card.timeline.map((entry) => entry.source))]
    const entries = source ? card.timeline.filter((entry) => entry.source === source) : card.timeline
    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography level="body-sm" textColor="text.tertiary">
                    What the card records about itself, newest first: a value changing or confirmed, a member or contact first stated by a source.
                </Typography>
                <Select size="sm" value={source} onChange={(_, v) => setSource(v ?? '')} sx={{ minWidth: 160 }}>
                    <Option value="">Every source</Option>
                    {sources.map((s) => (
                        <Option key={s} value={s}>
                            {s}
                        </Option>
                    ))}
                </Select>
            </Box>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm" stripe="odd">
                    <thead>
                        <tr>
                            <th style={{ width: 190 }}>When</th>
                            <th style={{ width: 120 }}>Event</th>
                            <th style={{ width: 170 }}>By</th>
                            <th>What</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, i) => (
                            <tr key={i}>
                                <td>{when(entry.at)}</td>
                                <td>
                                    <Chip
                                        size="sm"
                                        variant="soft"
                                        color={entry.event === 'CHANGED' ? 'primary' : entry.event === 'REGISTERED' ? 'success' : 'neutral'}
                                    >
                                        {entry.event}
                                    </Chip>
                                </td>
                                <td>{[entry.type, entry.source].filter(Boolean).join(' · ')}</td>
                                <td>{entry.what}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>
        </>
    )
}

function Connections({ mc }: { mc: string }) {
    const { data, error, loading } = useLoadPipelineData(() => brokerPipelineApi.getConnections(mc), [mc])
    if (error) return <ErrorState error={error} />
    if (loading && !data) return <Loading />
    if (!data) return null
    if (data.connections.length === 0)
        return <Typography level="body-sm">No other brokerage shares a domain, an email or phone, or a family with MC {mc}.</Typography>
    return (
        <>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 1 }}>
                A shared value can be a family of brokerages, a factoring company or TMS mailbox serving many, or a wrong statement worth a look.
            </Typography>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm" stripe="odd">
                    <thead>
                        <tr>
                            <th style={{ width: 90 }}>Shared</th>
                            <th>Value</th>
                            <th style={{ width: 90 }}>Others</th>
                            <th>Brokerages</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.connections.map((c) => (
                            <tr key={`${c.kind}|${c.value}`}>
                                <td>
                                    <Chip size="sm" variant="soft">
                                        {c.kind}
                                    </Chip>
                                </td>
                                <td>
                                    <code>{c.value}</code>
                                </td>
                                <td>{formatNumber(c.count)}</td>
                                <td>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {c.brokerages.map((b) => (
                                            <Link key={b.mc} component={RouterLink} to={brokeragePath(b.mc)} level="body-sm">
                                                {b.mc}
                                                {b.legalName ? ` ${b.legalName}` : ''}
                                            </Link>
                                        ))}
                                        {c.count > c.brokerages.length ? (
                                            <Typography level="body-sm" textColor="text.tertiary">
                                                and {formatNumber(c.count - c.brokerages.length)} more
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>
        </>
    )
}

export default function BrokeragePage() {
    const { mc = '' } = useParams<{ mc: string }>()
    const [params, setParams] = useSearchParams()
    const view: View = (VIEWS as readonly string[]).includes(params.get('view') ?? '') ? (params.get('view') as View) : 'overview'
    const cohort = params.get('cohort') ?? ''
    const page = Math.max(0, Number(params.get('page') ?? 0) || 0)
    const set = (next: Record<string, string>) =>
        setParams(
            Object.fromEntries(Object.entries({ view, cohort, page: String(page), ...next }).filter(([key, v]) => v !== '' && !(key === 'page' && v === '0')))
        )
    const { data: card, error, loading } = useLoadPipelineData(() => brokerPipelineApi.getBrokerage(mc, cohort || undefined, page), [mc, cohort, page])
    const contacts = card ? Object.values(card.contactCounts).reduce((sum, n) => sum + n, 0) : 0

    return (
        <Box sx={{ p: 3 }}>
            <Link component={RouterLink} to="/broker-pipeline/consumer/brokerages" level="body-sm" sx={{ mb: 1 }}>
                ← Brokerages
            </Link>
            {error ? <ErrorState error={error} /> : null}
            {loading && !card ? <Loading /> : null}
            {card ? (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography level="h2">{card.legalName?.value ?? `MC ${card.mc}`}</Typography>
                        <Chip size="sm" variant="soft" color={statusColor(card.status)}>
                            {card.status}
                        </Chip>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Chip size="sm">MC {card.mc}</Chip>
                        {card.dot ? <Chip size="sm">DOT {card.dot}</Chip> : null}
                        {card.authority ? <Chip size="sm">authority {card.authority.value.status}</Chip> : null}
                        {card.family ? <Chip size="sm">family {card.family.value.parentName}</Chip> : null}
                        <Typography level="body-xs" textColor="text.tertiary">
                            registered {when(card.registeredAt)} · updated {when(card.updatedAt)}
                        </Typography>
                    </Box>
                    <StatRow>
                        <Stat label="Contacts" value={formatNumber(contacts)} />
                        <Stat label="Sources" value={formatNumber(card.sources.length)} />
                        <Stat label="Observations" value={formatNumber(card.observedCount)} />
                        <Stat label="Trade names" value={formatNumber(card.tradeNames.length)} />
                        <Stat label="Domains" value={formatNumber(card.domains.length)} />
                        <Stat label="Last observed" value={card.lastObservedAt ? new Date(card.lastObservedAt).toLocaleDateString() : '—'} />
                    </StatRow>

                    <Tabs value={view} onChange={(_, v) => set({ view: String(v), page: '0' })} sx={{ bgcolor: 'transparent', mt: 2 }}>
                        <TabList tabFlex="none" sx={{ mb: 2 }}>
                            {VIEWS.map((v) => (
                                <Tab key={v} value={v} variant="plain" color="neutral">
                                    {VIEW_LABELS[v]}
                                </Tab>
                            ))}
                        </TabList>
                    </Tabs>

                    {view === 'overview' ? <Overview card={card} /> : null}
                    {view === 'contacts' ? (
                        <Contacts
                            card={card}
                            cohort={cohort}
                            page={page}
                            onCohort={(c) => set({ cohort: c, page: '0' })}
                            onPage={(p) => set({ page: String(p) })}
                        />
                    ) : null}
                    {view === 'sources' ? <Sources card={card} /> : null}
                    {view === 'timeline' ? <Timeline card={card} /> : null}
                    {view === 'connections' ? <Connections mc={card.mc} /> : null}
                </>
            ) : null}
        </Box>
    )
}
